<script setup>
import { reactive } from 'vue'
import { api as viewerApi } from 'v-viewer'

const props = defineProps({
    attachments: { type: Array, default: () => [] },
    // Une carte par ligne, en pleine largeur (colonnes étroites)
    stacked: { type: Boolean, default: false },
    // Bouton de téléchargement sur chaque fichier
    downloadable: { type: Boolean, default: false },
    // Force l'affichage de la suppression (sinon : droit « delete » de chaque fichier)
    canDelete: { type: Boolean, default: null },
    // Tuiles avec vignette des images (bibliothèque de médias : les fichiers sont le contenu)
    previews: { type: Boolean, default: false },
})

// Vignette introuvable : on affiche l'icône à la place d'un cadre vide
const brokenPreviews = reactive(new Set())
const showPreview = (attachment) => props.previews && isImage(attachment) && !brokenPreviews.has(attachment.data.id)

const deletable = (attachment) => props.canDelete ?? attachment.can?.delete ?? false

const emit = defineEmits(['delete'])

// Aperçu pour une image, téléchargement sinon
const isImage = (attachment) => attachment.data.mime_type?.includes('image')

function open(attachment) {
    if (isImage(attachment)) {
        viewerApi({ images: [attachment.data.path], options: { toolbar: false, transition: false, loading: true } })
    } else {
        window.open(attachment.data.path)
    }
}

// Une icône par type de fichier, et une icône par défaut
function icon(attachment) {
    const mime = attachment.data.mime_type ?? ''
    if (mime.includes('image')) return 'mdi-file-image text-info'
    if (mime.includes('pdf')) return 'mdi-file-pdf-box text-danger'
    if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return 'mdi-file-excel-box text-success'
    if (mime.includes('word') || mime.includes('document')) return 'mdi-file-word-box text-primary'
    if (mime.includes('zip') || mime.includes('compressed')) return 'mdi-folder-zip text-warning'
    if (mime.includes('video')) return 'mdi-file-video text-info'
    if (mime.includes('audio')) return 'mdi-file-music text-info'
    return 'mdi-file-document-outline text-secondary'
}
</script>

<template>
    <div class="attachment-files" :class="{ 'is-stacked': stacked && !previews, 'is-previews': previews }" v-if="attachments?.length">
        <div
            v-for="attachment in attachments"
            :key="attachment.data.id"
            :id="'attachment' + attachment.data.id"
            class="attachment-file"
            role="button"
            :title="attachment.data.name"
            @click="open(attachment)"
        >
            <!-- Icône par défaut ; vignette en mode aperçu. Un clic sur une image l'ouvre en grand -->
            <img
                v-if="showPreview(attachment)"
                :src="'/format-image/' + attachment.data.path + '?w=360&h=240&fm=webp&fit=crop'"
                class="attachment-file-preview"
                alt=""
                loading="lazy"
                @error="brokenPreviews.add(attachment.data.id)"
            />
            <span v-else class="attachment-file-icon"><i class="mdi" :class="icon(attachment)"></i></span>
            <span class="attachment-file-info">
                <span class="attachment-file-name">{{ attachment.data.name }}</span>
                <span class="attachment-file-size">{{ attachment.data.size }}</span>
            </span>
            <a :href="attachment.data.path" target="_blank" download class="attachment-file-download" title="Télécharger" @click.stop v-if="downloadable">
                <i class="ri-download-2-line"></i>
            </a>
            <button type="button" class="attachment-file-delete" title="Supprimer le fichier" @click.stop="emit('delete', attachment.data.id)" v-if="deletable(attachment)">
                <i class="mdi mdi-close"></i>
            </button>
        </div>
    </div>
</template>

<style scoped>
.attachment-files {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.attachment-file {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 16rem;
    padding: 0.4rem 0.6rem 0.4rem 0.4rem;
    border: 1px solid var(--vz-border-color);
    border-radius: 0.5rem;
    background-color: var(--vz-secondary-bg);
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.attachment-file:hover {
    border-color: var(--vz-secondary);
    background-color: var(--vz-light);
}

.attachment-file-icon {
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.35rem;
}

.attachment-file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--vz-light);
    font-size: 1.4rem;
}

.attachment-file-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
}

.attachment-file-name {
    overflow: hidden;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--vz-body-color);
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-file-size {
    font-size: 0.7rem;
    color: var(--vz-secondary-color);
}

/* Mode aperçu : tuiles en grille, vignette en haut */
.attachment-files.is-previews {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
}

.attachment-files.is-previews .attachment-file {
    flex-wrap: wrap;
    max-width: none;
    padding: 0.4rem;
}

.attachment-files.is-previews .attachment-file-icon {
    width: 100%;
    height: 7rem;
    font-size: 2.5rem;
}

.attachment-file-preview {
    width: 100%;
    height: 7rem;
    border-radius: 0.35rem;
    object-fit: cover;
    background-color: var(--vz-light);
}

.attachment-files.is-previews .attachment-file-info {
    padding: 0 0.2rem;
}

.attachment-files.is-stacked {
    flex-direction: column;
    margin-top: 0;
}

.attachment-files.is-stacked .attachment-file {
    max-width: none;
}

.attachment-file-info {
    flex-grow: 1;
}

.attachment-file-download {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    font-size: 1rem;
    color: var(--vz-secondary-color);
    transition: background-color 0.15s ease, color 0.15s ease;
}

.attachment-file-download:hover {
    background-color: var(--vz-secondary-bg-subtle);
    color: var(--vz-secondary);
}

.attachment-file-delete {
    position: absolute;
    top: -0.45rem;
    right: -0.45rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;
    height: 1.2rem;
    padding: 0;
    border: 1px solid var(--vz-border-color);
    border-radius: 50%;
    background-color: var(--vz-secondary-bg);
    font-size: 0.75rem;
    color: var(--vz-danger);
    opacity: 0;
    transition: opacity 0.15s ease;
}

.attachment-file:hover .attachment-file-delete {
    opacity: 1;
}

/* Écrans tactiles : pas de survol, la croix reste visible */
@media (hover: none) {
    .attachment-file-delete {
        opacity: 1;
    }
}
</style>
